import React from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { 
  DollarSign,
  CreditCard, 
  Receipt, 
  BarChart3,
  ChevronRight,
  Search,
  Download
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * Business Workspace
 * 
 * Contains: Payroll, Billing, Financials, Reports
 */

const BUSINESS_SECTIONS = [
  {
    id: 'payroll',
    name: 'Payroll',
    description: 'Run payroll and manage compensation',
    icon: CreditCard,
    path: '/business/payroll',
    color: 'bg-emerald-500',
    stats: { label: 'Next Run', value: '—' }
  },
  {
    id: 'billing',
    name: 'Billing',
    description: 'Invoices and accounts receivable',
    icon: Receipt,
    path: '/business/billing',
    color: 'bg-blue-500',
    stats: { label: 'Outstanding', value: '—' }
  },
  {
    id: 'reports',
    name: 'Reports',
    description: 'Financial reports and analytics',
    icon: BarChart3,
    path: '/business/reports',
    color: 'bg-purple-500',
    stats: null
  },
];

export default function BusinessWorkspace() {
  const location = useLocation();
  const isLanding = location.pathname === '/business';

  // If we're on a sub-route, render the Outlet
  if (!isLanding) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-[#FBFBFA]">
      <div className="max-w-6xl mx-auto px-6 py-8">
        
        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-semibold text-[#0D1B33]">Business</h1>
            <div className="flex items-center gap-3">
              <Link to="/business/payroll">
                <Button className="bg-[#176DFF] hover:bg-[#176DFF]/90">
                  <DollarSign className="w-4 h-4 mr-2" />
                  Run Payroll
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-[#64748B]">
            Payroll, billing, and financial operations.
          </p>
        </header>

        {/* Financial Summary */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] p-6 mb-6">
          <h2 className="font-semibold text-[#0D1B33] mb-4">Financial Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-[#64748B] mb-1">This Period Revenue</p>
              <p className="text-2xl font-semibold text-[#0D1B33]">—</p>
            </div>
            <div>
              <p className="text-sm text-[#64748B] mb-1">Payroll Expense</p>
              <p className="text-2xl font-semibold text-[#0D1B33]">—</p>
            </div>
            <div>
              <p className="text-sm text-[#64748B] mb-1">Outstanding Invoices</p>
              <p className="text-2xl font-semibold text-[#0D1B33]">—</p>
            </div>
            <div>
              <p className="text-sm text-[#64748B] mb-1">Gross Margin</p>
              <p className="text-2xl font-semibold text-[#0D1B33]">—</p>
            </div>
          </div>
        </div>

        {/* Section Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {BUSINESS_SECTIONS.map((section) => (
            <Link
              key={section.id}
              to={section.path}
              className="bg-white rounded-xl border border-[#E2E8F0] p-5 hover:border-[#176DFF]/30 hover:shadow-sm transition-all group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white ${section.color}`}>
                  <section.icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-[#CBD5E1] group-hover:text-[#176DFF] group-hover:translate-x-1 transition-all" />
              </div>
              <h3 className="font-semibold text-[#0D1B33] mb-1">{section.name}</h3>
              <p className="text-sm text-[#64748B] mb-3">{section.description}</p>
              {section.stats && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#64748B]">{section.stats.label}:</span>
                  <span className="font-semibold text-[#0D1B33]">{section.stats.value}</span>
                </div>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
